import React, { useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { AuthContext } from '../../App';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { CheckCircle2, ClipboardList, AlertTriangle, Circle, Users, User as UserIcon } from 'lucide-react';
import { toast } from 'sonner';

// Cohort Progress — two lenses on the same completion + gate data:
//   • Per-course:  users × lessons matrix, sorted by completion desc
//   • Per-learner: courses × lessons matrix for one selected learner
// Toggle at the top switches between them without re-fetching lookup lists.

const ASSN_META = {
  none:        { cls: 'bg-slate-100 text-slate-500 border-slate-300',       label: '—',       Icon: Circle },
  pass:        { cls: 'bg-emerald-100 text-emerald-800 border-emerald-500', label: 'Pass',    Icon: CheckCircle2 },
  submitted:   { cls: 'bg-sky-100 text-sky-800 border-sky-500',             label: 'Submitted', Icon: ClipboardList },
  missing:     { cls: 'bg-amber-100 text-amber-800 border-amber-500',       label: 'Missing', Icon: AlertTriangle },
  needs_work:  { cls: 'bg-rose-100 text-rose-800 border-rose-500',          label: 'Redo',    Icon: AlertTriangle },
};

const StatusDot = ({ cell }) => {
  // Priority: needs_work > checklist_pending > !completed > completed.
  if (cell.assignment_state === 'needs_work') {
    const m = ASSN_META.needs_work;
    return (
      <span title="Assignment needs work" className={`inline-flex items-center justify-center w-6 h-6 rounded-full border ${m.cls}`}>
        <m.Icon className="w-3 h-3" />
      </span>
    );
  }
  if (cell.checklist_pending?.length) {
    return (
      <span title={`Checklist pending: ${cell.checklist_pending.join(', ')}`}
            className="inline-flex items-center justify-center w-6 h-6 rounded-full border bg-amber-100 text-amber-800 border-amber-500">
        <ClipboardList className="w-3 h-3" />
      </span>
    );
  }
  if (cell.completed) {
    return (
      <span title="Completed" className="inline-flex items-center justify-center w-6 h-6 rounded-full border bg-emerald-100 text-emerald-800 border-emerald-500">
        <CheckCircle2 className="w-3 h-3" />
      </span>
    );
  }
  return (
    <span title="Not started" className="inline-flex items-center justify-center w-6 h-6 rounded-full border bg-slate-100 text-slate-400 border-slate-300">
      <Circle className="w-3 h-3" />
    </span>
  );
};

const CohortProgress = () => {
  const { API } = useContext(AuthContext);
  const [mode, setMode] = useState('course'); // 'course' | 'learner'
  const [courses, setCourses] = useState([]);
  const [users, setUsers] = useState([]);
  const [courseId, setCourseId] = useState('');
  const [userId, setUserId] = useState('');
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Bootstrap: load courses + users so the pickers are populated.
    axios.get(`${API}/lms/courses`).then((r) => {
      const list = r.data?.courses || [];
      setCourses(list);
      if (list.length && !courseId) setCourseId(list[0].id);
    }).catch(() => {});
    axios.get(`${API}/admin/users`).then((r) => {
      const list = (r.data?.users || r.data || []).filter((u) => u.role !== 'admin');
      setUsers(list);
      if (list.length && !userId) setUserId(list[0].id);
    }).catch(() => {});
    // eslint-disable-next-line
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = mode === 'course' ? { course_id: courseId } : { user_id: userId };
      const r = await axios.get(`${API}/admin/lms/cohort-progress`, { params });
      setPayload(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load cohort progress');
      setPayload(null);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if ((mode === 'course' && courseId) || (mode === 'learner' && userId)) load();
    // eslint-disable-next-line
  }, [mode, courseId, userId]);

  return (
    <div className="max-w-7xl mx-auto p-6" data-testid="cohort-progress-page">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight">Cohort Progress</h1>
          <p className="text-sm text-muted-foreground">
            Live "who's stuck where" — completion, checklist gates and pass/needs-work grades in one grid.
          </p>
        </div>
        {/* Mode toggle */}
        <div className="inline-flex rounded-full border border-border overflow-hidden" data-testid="cohort-mode-toggle">
          <button
            onClick={() => setMode('course')}
            className={`px-4 py-1.5 text-sm font-semibold flex items-center gap-1.5 ${mode === 'course' ? 'bg-primary text-primary-foreground' : 'bg-background hover:bg-muted'}`}
            data-testid="cohort-mode-course"
          >
            <Users className="w-3.5 h-3.5" /> Per course
          </button>
          <button
            onClick={() => setMode('learner')}
            className={`px-4 py-1.5 text-sm font-semibold flex items-center gap-1.5 ${mode === 'learner' ? 'bg-primary text-primary-foreground' : 'bg-background hover:bg-muted'}`}
            data-testid="cohort-mode-learner"
          >
            <UserIcon className="w-3.5 h-3.5" /> Per learner
          </button>
        </div>
      </div>

      {/* Pickers */}
      <div className="flex items-center gap-2 mb-3">
        {mode === 'course' ? (
          <select
            value={courseId}
            onChange={(e) => setCourseId(e.target.value)}
            className="text-sm border border-border rounded-md px-2 py-1.5 bg-background min-w-72"
            data-testid="cohort-course-picker"
          >
            {courses.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
          </select>
        ) : (
          <select
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            className="text-sm border border-border rounded-md px-2 py-1.5 bg-background min-w-72"
            data-testid="cohort-user-picker"
          >
            {users.map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
          </select>
        )}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-3 text-xs mb-3 text-muted-foreground">
        <LegendChip cls="bg-emerald-100 text-emerald-800 border-emerald-500" label="Completed" Icon={CheckCircle2} />
        <LegendChip cls="bg-amber-100 text-amber-800 border-amber-500" label="Checklist pending" Icon={ClipboardList} />
        <LegendChip cls="bg-rose-100 text-rose-800 border-rose-500" label="Assignment needs work" Icon={AlertTriangle} />
        <LegendChip cls="bg-slate-100 text-slate-400 border-slate-300" label="Not started" Icon={Circle} />
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          {loading && <div className="p-6 text-sm text-muted-foreground">Loading…</div>}
          {!loading && payload && mode === 'course' && (
            <CourseMatrix payload={payload} />
          )}
          {!loading && payload && mode === 'learner' && (
            <LearnerMatrix payload={payload} />
          )}
          {!loading && !payload && (
            <div className="p-6 text-sm text-muted-foreground">
              {mode === 'course'
                ? 'Pick a course above to see enrolled learners × lessons.'
                : 'Pick a learner to see their journey.'}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const LegendChip = ({ cls, label, Icon }) => (
  <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 ${cls}`}>
    <Icon className="w-3 h-3" /> {label}
  </span>
);

const CourseMatrix = ({ payload }) => {
  const lessons = payload.lessons || [];
  const rows = payload.rows || [];
  if (!rows.length) {
    return <div className="p-6 text-sm text-muted-foreground">No learners enrolled yet in <strong>{payload.course?.title}</strong>.</div>;
  }
  return (
    <table className="w-full text-sm" data-testid="cohort-course-matrix">
      <thead className="bg-muted/40 text-xs uppercase tracking-widest text-muted-foreground">
        <tr>
          <th className="text-left p-3 sticky left-0 bg-muted/40 z-10">Learner</th>
          <th className="text-left p-3">Completion</th>
          {lessons.map((l) => (
            <th key={l.id} className="p-2 text-center min-w-28" title={l.title}>
              <div className="text-[10px] font-bold opacity-75 truncate">{l.module_title || ''}</div>
              <div className="text-[11px] font-semibold truncate">{l.title}</div>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.user_id} className="border-t border-border hover:bg-muted/20" data-testid={`cohort-row-${r.user_id}`}>
            <td className="p-3 sticky left-0 bg-background z-10">
              <div className="font-semibold text-sm">{r.name || r.email}</div>
              {r.name && <div className="text-[11px] text-muted-foreground">{r.email}</div>}
            </td>
            <td className="p-3">
              <div className="flex items-center gap-2 min-w-32">
                <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
                  <div className="h-full bg-emerald-500" style={{ width: `${r.completion_pct}%` }} />
                </div>
                <span className="text-xs font-bold w-10 text-right">{r.completion_pct}%</span>
              </div>
            </td>
            {r.cells.map((c) => (
              <td key={c.lesson_id} className="p-2 text-center">
                <StatusDot cell={c} />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const LearnerMatrix = ({ payload }) => {
  const courses = payload.courses || [];
  if (!courses.length) {
    return <div className="p-6 text-sm text-muted-foreground">
      {payload.user?.name || payload.user?.email || 'This learner'} isn't enrolled in any courses yet.
    </div>;
  }
  return (
    <div className="p-4 space-y-4" data-testid="cohort-learner-matrix">
      {courses.map((c) => (
        <div key={c.course_id} className="rounded-lg border border-border" data-testid={`learner-course-${c.course_id}`}>
          <div className="flex items-center justify-between px-3 py-2 bg-muted/30 border-b border-border">
            <div className="font-semibold text-sm">{c.course_title}</div>
            <div className="flex items-center gap-2">
              <div className="w-24 h-1.5 rounded-full bg-muted overflow-hidden">
                <div className="h-full bg-emerald-500" style={{ width: `${c.completion_pct}%` }} />
              </div>
              <span className="text-xs font-bold">{c.completion_pct}%</span>
            </div>
          </div>
          <div className="grid gap-2 p-3 grid-cols-[repeat(auto-fill,minmax(220px,1fr))]">
            {c.cells.map((cell) => (
              <div key={cell.lesson_id} className="flex items-center gap-2 rounded-md border border-border p-2">
                <StatusDot cell={cell} />
                <div className="min-w-0">
                  <div className="text-[11px] uppercase tracking-widest opacity-60 truncate">{cell.module_title}</div>
                  <div className="text-xs font-semibold truncate">{cell.lesson_title}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default CohortProgress;
